import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'

export default function FinancingOptionsPage() {
  const loanTypes = [
    {
      name: 'Conventional (Fannie/Freddie)',
      downPayment: '20-25%',
      rates: 'Best available',
      term: '15-30 years',
      maxProperties: '10 financed properties',
      pros: ['Lowest rates available', 'Fixed-rate options', 'Long amortization reduces monthly payments'],
      cons: [
        'Strict qualification (DTI, credit score, reserves)',
        'Limit of 10 financed properties per borrower',
        'Full documentation required (tax returns, W-2s)',
        'Slower closing (30-45 days)',
      ],
      bestFor: 'Your first 1-4 investment properties when you have strong W-2 income and clean financials.',
    },
    {
      name: 'DSCR Loan',
      downPayment: '20-25%',
      rates: 'Conv. + 0.5-1.5%',
      term: '30 years (typical)',
      maxProperties: 'No limit',
      pros: [
        'Qualifies on property income, not personal income',
        'No limit on number of properties',
        'No tax returns or employment verification',
        'Close in LLC (asset protection)',
      ],
      cons: [
        'Higher rates than conventional',
        'Typically requires 1.20-1.25x DSCR',
        'Higher down payment on some programs',
        'Prepayment penalties are common',
      ],
      bestFor: 'Scaling beyond 10 properties, self-employed investors, or when you want to keep properties in an LLC.',
    },
    {
      name: 'Commercial / Portfolio',
      downPayment: '25-30%',
      rates: 'Varies (often adjustable)',
      term: '5-7 year balloon, 20-25yr amortization',
      maxProperties: 'No limit',
      pros: [
        'Flexible terms (negotiable)',
        'Relationship-based (local banks)',
        'Can finance 5+ unit properties',
        'Portfolio lenders may be more creative',
      ],
      cons: [
        'Balloon payments create refinance risk',
        'Shorter amortization increases monthly payments',
        'Often adjustable rate',
        'Requires strong banking relationship',
      ],
      bestFor:
        'Larger multifamily (5+ units), portfolio acquisitions, or when you have a strong relationship with a local bank.',
    },
    {
      name: 'Hard Money / Private',
      downPayment: '10-30%',
      rates: '10-14%',
      term: '6-24 months',
      maxProperties: 'No limit',
      pros: [
        'Fast closing (7-14 days)',
        'Less documentation',
        'Based on deal, not borrower',
        'Good for BRRRR acquisitions',
      ],
      cons: [
        'Very high rates',
        'Short term (must refinance or sell)',
        'Origination fees (2-4 points)',
        'Not a long-term hold solution',
      ],
      bestFor:
        'BRRRR strategy: acquire and rehab with hard money, then refinance into a permanent loan once stabilized.',
    },
    {
      name: 'Seller Financing',
      downPayment: 'Negotiable (5-20%)',
      rates: 'Negotiable',
      term: 'Negotiable (often 3-7 year balloon)',
      maxProperties: 'No limit',
      pros: ['Most flexible terms possible', 'No bank qualification', 'Faster closing', 'Can structure creative terms'],
      cons: [
        'Depends on finding willing seller',
        'Balloon payment risk',
        'May not have same consumer protections',
        'Usually shorter term',
      ],
      bestFor:
        'Off-market deals where the seller owns the property free and clear and is motivated by cash flow rather than a lump sum.',
    },
  ]

  return (
    <div className="mx-auto max-w-4xl space-y-8 p-4 py-12">
      <Link to="/resources" className="prose-link">
        &larr; Back to Resources
      </Link>
      <h1 className="text-fluid-h2 text-navy">Financing the First Few Deals</h1>

      <Card>
        <CardContent className="p-6">
          <p className="text-lg">
            How you finance a deal determines your returns as much as the property itself. A difference of 1% in rate or
            5% in down payment can swing cash-on-cash returns by several points. Here's what's available for small
            multifamily investors.
          </p>
        </CardContent>
      </Card>

      <div className="space-y-6">
        {loanTypes.map((loan, i) => (
          <div key={loan.name}>
            {i > 0 && <Separator />}
            <Card>
              <CardContent className="space-y-4 p-6">
                <h2 className="text-2xl font-semibold">{loan.name}</h2>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="text-muted-foreground text-xs">Down Payment</div>
                    <div className="font-semibold">{loan.downPayment}</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="text-muted-foreground text-xs">Rates</div>
                    <div className="font-semibold">{loan.rates}</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="text-muted-foreground text-xs">Term</div>
                    <div className="font-semibold">{loan.term}</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="text-muted-foreground text-xs">Property Limit</div>
                    <div className="font-semibold">{loan.maxProperties}</div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <h3 className="mb-2 font-medium text-success">Pros</h3>
                    <ul className="text-muted-foreground space-y-1 text-sm">
                      {loan.pros.map((pro) => (
                        <li key={pro} className="flex gap-2">
                          <span className="shrink-0 text-success">+</span> {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="mb-2 font-medium text-destructive">Cons</h3>
                    <ul className="text-muted-foreground space-y-1 text-sm">
                      {loan.cons.map((con) => (
                        <li key={con} className="flex gap-2">
                          <span className="shrink-0 text-destructive">-</span> {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="rounded-lg border border-border bg-muted p-3">
                  <p className="text-sm">
                    <strong>Best for:</strong> {loan.bestFor}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-3 p-6">
          <h2 className="text-xl font-semibold">Which loan type does Fairmeadow use?</h2>
          <p className="text-muted-foreground">
            For our first deals, we use conventional financing to get the best rates. As we scale past 10 properties,
            we'll transition to DSCR loans to qualify on property income rather than personal DTI. For BRRRR-strategy
            acquisitions, we may use hard money for the acquisition and rehab phase, then refinance into a permanent
            loan once the property is stabilized and re-appraised.
          </p>
          <Link to="/resources/calculators/mortgage-payment" className="mt-2 inline-block prose-link">
            Run the numbers with our Mortgage Calculator &rarr;
          </Link>
        </CardContent>
      </Card>
    </div>
  )
}
